/**
 * What the Worker may return, and nothing else.
 *
 * **Sola's approved response shape, never Gemini's** (D-093, step 9). The
 * Worker constructs the Gemini request, parses the reply, validates it
 * structurally, applies the semantic checks and then returns *this* — so the
 * client never sees a provider-shaped object and cannot start depending on one.
 * Swapping the provider later is a Worker change, not a contract change.
 */

import type { GeminiFailure } from './errors'
import type { WireDomain } from './request'

/**
 * Agreement with **Sola's opinion**, which Gemini is shown.
 *
 * This is D-008's "asks ChatGPT to agree/disagree", unchanged in substance and
 * only in transport. The rules engine forms its opinion first, deterministically,
 * and Gemini comments on a plan that already exists.
 */
export const GEMINI_VERDICTS = ['agrees', 'partlyAgrees', 'disagrees'] as const
export type GeminiVerdict = (typeof GEMINI_VERDICTS)[number]

/**
 * The closed vocabulary of things a proposal may suggest.
 *
 * **`weeklyCommitment` is the whole of it** (D-093 §6, D-094 §1). The phase
 * type, the target rate, the route, the condition band and every rules-engine
 * input are **not suggestible and never become so** — a suggestion is precisely
 * the shape route input would take if it ever arrived, and D-062 forbids that
 * permanently.
 *
 * Adding a kind here is a contract change with its own decision, not a new
 * union member someone appends.
 */
export const SUGGESTION_KINDS = ['weeklyCommitment'] as const
export type SuggestionKind = (typeof SUGGESTION_KINDS)[number]

export interface WeeklyCommitmentSuggestion {
  readonly kind: 'weeklyCommitment'
  readonly domain: WireDomain
  readonly sessionsPerWeek: number
  readonly minutesPerSession?: number
}

export type GeminiSuggestion = WeeklyCommitmentSuggestion

/**
 * The longest rationale that may be returned.
 *
 * The Worker also caps output tokens through the structured-output schema it
 * supplies, because D-093's request-size ceiling **bounds input only** and the
 * "bounded spend" claim would otherwise be half true. This is the second half,
 * and it is enforced on the way out as well as requested on the way in — a
 * model that ignores an instruction is exactly what a validator is for.
 */
export const MAX_RATIONALE_CHARS = 1200

export interface GeminiProposal {
  readonly contractVersion: number
  readonly promptVersion: string
  /** Which model produced it, so a stored proposal can say. */
  readonly model: string
  readonly verdict: GeminiVerdict
  /** Gemini's own words. Displayed as returned, once the prose checks pass. */
  readonly rationale: string
  readonly suggestions: readonly GeminiSuggestion[]
}

/**
 * Everything the client can receive.
 *
 * A failure is a value, not an exception, and it carries the category the UI
 * renders. There is no third state: the Worker either returns a proposal that
 * passed every gate, or it says which gate stopped it.
 */
export type GeminiResponse =
  | { readonly ok: true; readonly proposal: GeminiProposal }
  | { readonly ok: false; readonly failure: GeminiFailure; readonly refusedRule?: RefusedRule }

/**
 * Which of D-094's five prose rules a response broke.
 *
 * Returned so the screen can **name the rule** rather than saying something
 * generic — *"the reply named a calorie target, which Project Sola never
 * shows"* is the difference between an app that looks broken and one that looks
 * principled. **The raw text is never returned with it** and is never stored:
 * showing it would defeat the gate, and keeping it would retain content that
 * was just judged unfit to show.
 */
export const REFUSED_RULES = [
  /* D-096 — neither Sola nor Gemini originates a calorie or protein number. */
  'calorieOrProteinFigure',
  /* D-062 and the quality bar — no score, grade, percentage-as-judgement or streak. */
  'scoreOrStreak',
  /* D-068 — no guaranteed outcome and no guaranteed date. */
  'guaranteedOutcome',
  /* D-015 — estimates, never diagnoses. */
  'medicalClaim',
  /* D-062, permanently — Gemini never touches the route. */
  'phaseOrRouteChange',
  /* A suggestion for a domain the user never activated (D-090). Structural
     rather than prose, but it refuses the response the same way. */
  'inactiveDomain',
] as const

export type RefusedRule = (typeof REFUSED_RULES)[number]

/** Human-facing copy for each rule, so two screens cannot explain one refusal differently. */
export const REFUSED_RULE_COPY: Record<RefusedRule, string> = {
  calorieOrProteinFigure:
    'The reply named a calorie or protein target. Project Sola never shows one — those come only from the system you named as your nutrition authority.',
  scoreOrStreak:
    'The reply scored or graded your week. Project Sola reports what happened and does not grade it.',
  guaranteedOutcome:
    'The reply promised a result or a date. Nothing here can be guaranteed, so Project Sola does not repeat a promise of one.',
  medicalClaim:
    'The reply made a medical claim. Project Sola deals in estimates and never in diagnoses.',
  phaseOrRouteChange:
    'The reply tried to change your phase or your route. Those are Project Sola’s to determine, and a second opinion does not get a vote on them.',
  inactiveDomain:
    'The reply gave advice about an area you have not taken on. Project Sola only plans what you asked it to.',
}
